// State-centric contractor delivery-memo flow ("our DM"). Shop Drawings and
// BOQs are PULLED from project state (linked to the payment group's activities)
// and shown read-only — the contractor only uploads proof photos + the invoice.
// Submit transitions DRAFT → SUBMITTED.
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
  styled,
} from "@mui/material";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import RequestQuoteOutlinedIcon from "@mui/icons-material/RequestQuoteOutlined";
import ArchitectureOutlinedIcon from "@mui/icons-material/ArchitectureOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import ConstructionOutlinedIcon from "@mui/icons-material/ConstructionOutlined";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import { ReactComponent as LeftArrowIcon } from "../../assets/svg/LeftArrowIcon.svg";
import UploadGrid from "../RequestForApproval/components/UploadGrid";
import BoqItemRow from "../../components/BoqItemRow/BoqItemRow";
import {
  uploadDeliveryFile,
  getOurDeliveryMemoById,
  getOurDeliveryMemoCreationData,
  createOurDeliveryMemo,
  updateOurDeliveryMemo,
  actionOurDeliveryMemo,
} from "../../services/ourDeliveryMemoService";
import {
  renderCurrencyIcon,
  getFormattedAmountWithoutCurrency,
} from "../../utils/CurrencyUtils";

const PrimaryButton = styled(Button)(() => ({
  backgroundColor: "#4D6BF6",
  color: "#fff",
  textTransform: "none",
  fontWeight: 500,
  borderRadius: "10px",
  boxShadow: "none",
  padding: "8px 18px",
  "&:hover": { backgroundColor: "#3B5BDB", boxShadow: "none" },
  "&.Mui-disabled": { backgroundColor: "#A8B5F8", color: "#fff" },
}));

const SecondaryButton = styled(Button)(() => ({
  backgroundColor: "#fff",
  color: "#0D0D0E",
  textTransform: "none",
  fontWeight: 500,
  borderRadius: "10px",
  border: "1px solid #D7D8D9",
  boxShadow: "none",
  padding: "8px 18px",
  "&:hover": { backgroundColor: "#F9FAFB", boxShadow: "none" },
  "&.Mui-disabled": { color: "#9CA3AF" },
}));

const StatusPill = ({ status }) => {
  const tone = String(status || "").toUpperCase();
  const palette =
    tone === "APPROVED"
      ? { bg: "#DCFCE7", fg: "#15803D" }
      : tone === "REJECTED" || tone === "DISPUTED"
      ? { bg: "#FEE2E2", fg: "#B91C1C" }
      : tone === "SUBMITTED" || tone === "CREATED"
      ? { bg: "#DBEAFE", fg: "#1D4ED8" }
      : { bg: "#F1F2F4", fg: "#374151" };
  return (
    <Box
      sx={{
        fontSize: 12,
        fontWeight: 600,
        backgroundColor: palette.bg,
        color: palette.fg,
        borderRadius: "999px",
        px: 1.25,
        py: 0.25,
      }}
    >
      {tone || "DRAFT"}
    </Box>
  );
};

// Titled white card matching the upload sections' look.
const SectionCard = ({ icon, title, subtitle, children }) => (
  <Box
    sx={{
      backgroundColor: "#fff",
      border: "1px solid #E5E7EB",
      borderRadius: "12px",
      padding: { xs: "16px", md: "20px 24px" },
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 0.25 }}>
      {icon}
      <Typography sx={{ fontSize: 16, fontWeight: 600, color: "#0D0D0E" }}>
        {title}
      </Typography>
    </Box>
    {subtitle && (
      <Typography sx={{ fontSize: 12, color: "#949DAA", mb: 1.5 }}>
        {subtitle}
      </Typography>
    )}
    {children}
  </Box>
);

const isPdfExt = (ext) => String(ext || "").toLowerCase().includes("pdf");

// One pulled shop-drawing tile — clickable, opens the file in a new tab.
const DrawingTile = ({ drawing }) => {
  const pdf = isPdfExt(drawing.extension);
  const caption =
    Array.isArray(drawing.activity_names) && drawing.activity_names.length
      ? drawing.activity_names.join(", ")
      : drawing.activity_name;
  return (
    <a
      href={drawing.image_url}
      target="_blank"
      rel="noreferrer"
      style={{ textDecoration: "none" }}
    >
      <Box
        sx={{
          width: 110,
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
        }}
      >
        <Box
          sx={{
            width: 110,
            height: 110,
            borderRadius: "10px",
            border: "1px solid #E5E7EB",
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#F9FAFB",
          }}
        >
          {pdf ? (
            <InsertDriveFileOutlinedIcon sx={{ fontSize: 40, color: "#4D6BF6" }} />
          ) : (
            <img
              src={drawing.image_url}
              alt={drawing.activity_name || "drawing"}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          )}
        </Box>
        {caption && (
          <Typography
            sx={{
              fontSize: 11,
              color: "#646F86",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            title={caption}
          >
            {caption}
          </Typography>
        )}
      </Box>
    </a>
  );
};

// Money is rendered with the Dhiram symbol (or the locale's currency text) so the
// whole screen matches the BOQ rows — never the literal "AED" prefix.
const fmtAmount = (value, iconType = "black-xs-small") => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "-";
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 2 }}>
      {renderCurrencyIcon(iconType)}
      {getFormattedAmountWithoutCurrency(n)}
    </span>
  );
};

// An activity with no amount assigned comes back as null, "" or 0 depending on
// the endpoint — all of them mean "not set", so show a dash instead of "0".
const fmtActivityAmount = (value) => {
  const n = Number(value);
  if (value === null || value === undefined || value === "") return "—";
  if (!Number.isFinite(n) || n === 0) return "—";
  return fmtAmount(n);
};

const fmtDate = (d) => {
  if (!d) return null;
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return null;
  return dt.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const prettyStatus = (s) =>
  String(s || "")
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const activityStatusPalette = (status) => {
  const s = String(status || "").toLowerCase().replace(/[-\s]/g, "_");
  if (s === "completed" || s === "approved") return { bg: "#DCFCE7", fg: "#15803D" };
  if (s === "in_progress" || s === "ongoing") return { bg: "#DBEAFE", fg: "#1D4ED8" };
  if (s === "delayed" || s === "due") return { bg: "#FEF3C7", fg: "#B45309" };
  if (s === "in_review" || s === "rejected") return { bg: "#FEE2E2", fg: "#B91C1C" };
  return { bg: "#F1F2F4", fg: "#374151" };
};

const activityTypeEmoji = (t) => {
  const s = String(t || "").toLowerCase();
  if (s === "design") return "🎨";
  if (s === "installation" || s === "renovation") return "🔧";
  if (s === "procurement") return "📦";
  if (s === "delivery") return "🚚";
  return "📦";
};

// Shared column template so header + rows stay aligned.
const ACT_GRID = "2.4fr 1fr 1.05fr 1.05fr 0.85fr 1.05fr 1.25fr";

const ActHeader = ({ children, right }) => (
  <Typography
    sx={{ fontSize: 11, color: "#949DAA", fontWeight: 600, textAlign: right ? "right" : "left" }}
  >
    {children}
  </Typography>
);

const ActCellText = ({ children, muted, right, title }) => (
  <Typography
    sx={{
      fontSize: 13,
      color: muted ? "#646F86" : "#0D0D0E",
      textAlign: right ? "right" : "left",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    }}
    title={title}
  >
    {children}
  </Typography>
);

// Compact read-only activity table (header row + aligned columns).
const ActivitiesTable = ({ activities }) => (
  <Box sx={{ border: "1px solid #E5E7EB", borderRadius: "12px", overflow: "hidden" }}>
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: ACT_GRID,
        gap: 1.5,
        px: 2,
        py: 1.25,
        backgroundColor: "#F9FAFB",
        borderBottom: "1px solid #E5E7EB",
      }}
    >
      <ActHeader>Activity</ActHeader>
      <ActHeader>Type</ActHeader>
      <ActHeader>Start Date</ActHeader>
      <ActHeader>End Date</ActHeader>
      <ActHeader right>Amount</ActHeader>
      <ActHeader>Status</ActHeader>
      <ActHeader>Dependencies</ActHeader>
    </Box>

    {activities.map((a) => {
      const pal = activityStatusPalette(a.status);
      const start = a.actual_start_dt || a.start_date;
      const end = a.actual_end_dt || a.end_date;
      const deps = a.dependencies?.length
        ? a.dependencies.map((d) => d.name || d.milestone_name).join(", ")
        : "None";
      return (
        <Box
          key={a.id}
          sx={{
            display: "grid",
            gridTemplateColumns: ACT_GRID,
            gap: 1.5,
            px: 2,
            py: 1.25,
            alignItems: "center",
            borderBottom: "1px solid #F1F2F4",
            "&:last-of-type": { borderBottom: "none" },
          }}
        >
          {/* Activity */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
            <Box
              sx={{
                width: 34,
                height: 34,
                flexShrink: 0,
                borderRadius: "8px",
                overflow: "hidden",
                border: "1px solid #E5E7EB",
                backgroundColor: "#F9FAFB",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {a.image_url ? (
                <img
                  src={a.image_url}
                  alt=""
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                <ConstructionOutlinedIcon sx={{ fontSize: 18, color: "#C7CDD6" }} />
              )}
            </Box>
            <ActCellText title={a.milestone_name || ""}>
              <span style={{ fontWeight: 500 }}>{a.milestone_name || "-"}</span>
            </ActCellText>
          </Box>

          {/* Type */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
            <span>{activityTypeEmoji(a.tags)}</span>
            <ActCellText muted title={a.tags || ""}>
              {a.tags || "-"}
            </ActCellText>
          </Box>

          {/* Dates */}
          <ActCellText>{fmtDate(start) || "—"}</ActCellText>
          <ActCellText>{fmtDate(end) || "—"}</ActCellText>

          {/* Amount */}
          <ActCellText right>
            <span style={{ fontWeight: 500 }}>
              {fmtActivityAmount(a.amount)}
            </span>
          </ActCellText>

          {/* Status */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                flexShrink: 0,
                backgroundColor: pal.fg,
              }}
            />
            <ActCellText title={prettyStatus(a.status)}>
              {prettyStatus(a.status)}
            </ActCellText>
          </Box>

          {/* Dependencies */}
          <ActCellText muted title={deps}>
            {deps}
          </ActCellText>
        </Box>
      );
    })}
  </Box>
);

const snagStatusPalette = (status) => {
  const s = String(status || "").toUpperCase();
  if (s === "RESOLVED" || s === "CLOSED") return { bg: "#DCFCE7", fg: "#15803D" };
  if (s === "OPEN") return { bg: "#FEE2E2", fg: "#B91C1C" };
  if (s === "FIXING" || s === "IN_PROGRESS") return { bg: "#FEF3C7", fg: "#B45309" };
  return { bg: "#F1F2F4", fg: "#374151" };
};

// Compact read-only issue/snag row.
const SnagRow = ({ snag }) => {
  const pal = snagStatusPalette(snag.status);
  const tags = [snag.category, snag.milestone_name, snag.source_type].filter(Boolean);
  const logged = fmtDate(snag.resolution_date || snag.created_at);
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        py: 1,
        borderBottom: "1px solid #F1F2F4",
        "&:last-of-type": { borderBottom: "none" },
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          flexShrink: 0,
          borderRadius: "8px",
          overflow: "hidden",
          border: "1px solid #E5E7EB",
          backgroundColor: "#F9FAFB",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {snag.image_url ? (
          <a href={snag.image_url} target="_blank" rel="noreferrer" style={{ display: "block", width: "100%", height: "100%" }}>
            <img
              src={snag.image_url}
              alt=""
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          </a>
        ) : (
          <ReportProblemOutlinedIcon sx={{ fontSize: 18, color: "#C7CDD6" }} />
        )}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 500,
            color: "#0D0D0E",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
          title={snag.description || ""}
        >
          {snag.description || "Issue"}
        </Typography>
        {tags.length > 0 && (
          <Typography
            sx={{
              fontSize: 11.5,
              color: "#646F86",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            title={tags.join("  ·  ")}
          >
            {tags.join("  ·  ")}
          </Typography>
        )}
      </Box>

      {snag.severity && (
        <Typography sx={{ fontSize: 11, color: "#646F86", whiteSpace: "nowrap" }}>
          {snag.severity}
        </Typography>
      )}
      {snag.status && (
        <Box
          sx={{
            flexShrink: 0,
            fontSize: 10,
            fontWeight: 600,
            color: pal.fg,
            backgroundColor: pal.bg,
            borderRadius: "999px",
            px: 0.9,
            py: 0.2,
          }}
        >
          {snag.status}
        </Box>
      )}
      {logged && (
        <Typography sx={{ fontSize: 11.5, color: "#646F86", whiteSpace: "nowrap", pl: 0.5 }}>
          {logged}
        </Typography>
      )}
    </Box>
  );
};

// Map server-side images / file entries to UploadGrid's expected shape.
const serverImageToGridItem = (img) => ({
  id: img.id,
  url: img.path || img.url,
  extension: img.file_extention || img.type || "image/jpeg",
  name: img.tags || "",
  mime_type: img.type || img.file_extention || null,
});

const isFileObject = (x) =>
  x && typeof x === "object" && typeof x.size === "number" && typeof x.name === "string";

export default function OurDeliveryMemo() {
  const { id, groupId } = useParams(); // id = proposal id, groupId = payment_groups_new.id
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const projectId = searchParams.get("project_id") || id;
  const proposalId = searchParams.get("proposal_id") || id;
  const memoIdParam = searchParams.get("memo_id");
  const forceView = searchParams.get("view") === "1";

  const [memoId, setMemoId] = useState(memoIdParam || null);
  const [memoStatus, setMemoStatus] = useState("");
  const [version, setVersion] = useState(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [paymentGroup, setPaymentGroup] = useState(null);
  // Pulled from project state (read-only).
  const [activities, setActivities] = useState([]);
  const [referenceDrawings, setReferenceDrawings] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [snags, setSnags] = useState([]);
  const [aiSummary, setAiSummary] = useState(null);

  // Contractor-provided.
  const [proofs, setProofs] = useState([]);
  const [invoice, setInvoice] = useState([]);
  const [notes, setNotes] = useState("");
  const [rejectReason, setRejectReason] = useState("");

  // DRAFT (not yet submitted) and REJECTED (sent back by admin) are editable so
  // the contractor can fix and re-submit. `view=1` still forces read-only.
  const editableStatuses = ["DRAFT", "REJECTED"];
  const isRejected = memoStatus.toUpperCase() === "REJECTED";
  const isReadOnly =
    forceView ||
    (!!memoStatus && !editableStatuses.includes(memoStatus.toUpperCase()));

  const backToProject = useCallback(() => {
    if (!proposalId) {
      navigate(-1);
      return;
    }
    // Return to the project's Billing Milestones tab in the "New" project view
    // (NewProjectDetailsImpl). Do NOT pass managed_by=contractor — that would
    // render the different ContractorProjectDetails screen.
    navigate(`/project/details/${proposalId}/billing-milestones`);
  }, [navigate, proposalId]);

  // Hydrate the pulled (read-only) sections + payment group from a preview or
  // rich-detail payload (both share these keys).
  const hydratePulled = useCallback((data) => {
    setPaymentGroup(data.payment_group || null);
    setActivities(Array.isArray(data.activities) ? data.activities : []);
    setReferenceDrawings(
      Array.isArray(data.reference_drawings) ? data.reference_drawings : []
    );
    setMaterials(Array.isArray(data.materials) ? data.materials : []);
    setSnags(Array.isArray(data.snags) ? data.snags : []);
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      if (memoId) {
        const data = await getOurDeliveryMemoById(memoId);
        const m = data.delivery_memo || {};
        const st = String(m.status || "").toUpperCase();
        setMemoStatus(m.status || "");
        setVersion(m.version ?? null);
        setAiSummary(m.ai_summary || null);
        // dispute_reason carries the admin's reject reason when REJECTED — show
        // it as a banner, not in the editable notes field. Otherwise it's the
        // contractor's own notes.
        if (st === "REJECTED") {
          setRejectReason(m.dispute_reason || "");
          setNotes("");
        } else {
          setRejectReason("");
          setNotes(m.dispute_reason || "");
        }
        hydratePulled(data);

        const imgs = Array.isArray(data.images) ? data.images : [];
        setProofs(imgs.map(serverImageToGridItem));

        if (m.invoice) {
          setInvoice([
            {
              id: `invoice-${memoId}`,
              url: m.invoice,
              extension: "application/pdf",
              name: "invoice.pdf",
            },
          ]);
        } else {
          setInvoice([]);
        }
      } else {
        setProofs([]);
        setInvoice([]);
        setNotes("");
        setRejectReason("");
        setMemoStatus("");
        setAiSummary(null);
        try {
          const creationData = await getOurDeliveryMemoCreationData(groupId);
          hydratePulled(creationData);
        } catch (e) {
          console.warn("Creation-data load failed:", e?.message);
        }
      }
    } catch (e) {
      console.error("Failed to load delivery memo", e);
      toast.error(e?.message || "Failed to load delivery memo");
    } finally {
      setLoading(false);
    }
  }, [memoId, groupId, hydratePulled]);

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memoId]);

  const uploadPendingAndCollect = useCallback(async () => {
    const proofPayload = [];
    for (const p of proofs) {
      if (isFileObject(p)) {
        const up = await uploadDeliveryFile(p);
        proofPayload.push({ url: up.url, mime_type: up.mime_type });
      } else if (p && (p.url || p.path)) {
        proofPayload.push({
          url: p.url || p.path,
          mime_type: p.mime_type || p.extension || null,
        });
      }
    }

    let invoiceUrl = null;
    if (invoice.length) {
      const head = invoice[0];
      if (isFileObject(head)) {
        const up = await uploadDeliveryFile(head);
        invoiceUrl = up.url;
      } else if (head?.url) {
        invoiceUrl = head.url;
      }
    }

    return { proofPayload, invoiceUrl };
  }, [proofs, invoice]);

  const persist = async () => {
    if (!invoice.length) {
      throw new Error("Invoice PDF is required");
    }
    const { proofPayload, invoiceUrl } = await uploadPendingAndCollect();
    if (memoId) {
      const res = await updateOurDeliveryMemo(memoId, {
        invoiceUrl,
        proofUrls: proofPayload,
        notes: notes || null,
      });
      const m = res?.memo || {};
      if (m.status) setMemoStatus(m.status);
      return String(memoId);
    }
    const res = await createOurDeliveryMemo({
      paymentGroupId: parseInt(groupId, 10),
      invoiceUrl,
      proofUrls: proofPayload,
      notes: notes || null,
    });
    const m = res?.memo || {};
    const newId = m.id;
    if (newId != null) {
      setMemoId(String(newId));
      setMemoStatus(m.status || "DRAFT");
      setVersion(m.version ?? 1);
    }
    return newId != null ? String(newId) : null;
  };

  const handleSave = async () => {
    if (saving || submitting) return;
    setSaving(true);
    try {
      await persist();
      toast.success("Delivery memo saved as draft");
      // Saving a draft closes the memo — go back to the Billing Milestones list.
      backToProject();
    } catch (e) {
      console.error("Save failed", e);
      toast.error(e?.message || "Failed to save delivery memo");
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (submitting || saving) return;
    setSubmitting(true);
    try {
      const idToSubmit = await persist();
      if (!idToSubmit) throw new Error("Memo id missing after save");
      // Contractor submit → CREATED (awaiting admin review).
      await actionOurDeliveryMemo(idToSubmit, "CREATED");
      toast.success("Delivery memo submitted");
      setMemoStatus("CREATED");
      backToProject();
    } catch (e) {
      console.error("Submit failed", e);
      toast.error(e?.message || "Failed to submit delivery memo");
    } finally {
      setSubmitting(false);
    }
  };

  const pgLabel = paymentGroup?.group_name || "Delivery Memo";
  const pgAmount = paymentGroup?.amount;

  if (loading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          padding: { xs: "2vh 4vw 1vh", md: "3vh 2.5vw 1.5vh" },
          gap: "10px",
          background:
            "linear-gradient(180deg, rgba(77, 107, 246, 0.30) 0%, rgba(255, 255, 255, 0.00) 100%)",
        }}
      >
        <LeftArrowIcon onClick={backToProject} style={{ cursor: "pointer" }} />
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, flexWrap: "wrap", flex: 1 }}>
          <Box sx={{ color: "#0D0D0E", fontSize: { xs: "15px", md: "16px" }, fontWeight: 500 }}>
            {isReadOnly ? "View Delivery Memo" : "Request for Payment"} — {pgLabel}
          </Box>
          {version != null && (
            <Box
              sx={{
                fontSize: 12,
                fontWeight: 600,
                color: "#0D0D0E",
                backgroundColor: "#F1F2F4",
                borderRadius: "999px",
                px: 1.25,
                py: 0.25,
              }}
            >
              Version-{version}
            </Box>
          )}
          {memoStatus && <StatusPill status={memoStatus} />}
        </Box>
        {pgAmount != null && (
          <Typography sx={{ fontSize: 18, fontWeight: 600, color: "#0D0D0E", whiteSpace: "nowrap" }}>
            {fmtAmount(pgAmount, "black-small")}
          </Typography>
        )}
      </Box>

      <Box sx={{ padding: { xs: "0 4vw 6vh", md: "0 2.5vw 6vh" }, display: "flex", flexDirection: "column", gap: 2.5 }}>
        {/* Reject reason — shown when the admin sent the memo back. */}
        {isRejected && rejectReason && (
          <Box
            sx={{
              backgroundColor: "#FEF2F2",
              border: "1px solid #FECACA",
              borderRadius: "12px",
              padding: "14px 18px",
            }}
          >
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#B91C1C", mb: 0.5 }}>
              Rejected by Reno — please fix and re-submit
            </Typography>
            <Typography sx={{ fontSize: 13, color: "#7F1D1D", whiteSpace: "pre-wrap" }}>
              {rejectReason}
            </Typography>
          </Box>
        )}

        {/* Shop Drawings — pulled from project state (linked via activities) */}
        <SectionCard
          icon={<ArchitectureOutlinedIcon sx={{ fontSize: 20, color: "#4D6BF6" }} />}
          title="Shop Drawings"
          subtitle="Pulled from the project — linked to this milestone's activities"
        >
          {referenceDrawings.length === 0 ? (
            <Typography sx={{ fontSize: 13, color: "#949DAA" }}>
              No shop drawings linked to these activities.
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
              {referenceDrawings.map((d) => (
                <DrawingTile key={d.id} drawing={d} />
              ))}
            </Box>
          )}
        </SectionCard>

        {/* BOQs — pulled from project state (Materials linked to activities) */}
        <SectionCard
          icon={<ReceiptLongOutlinedIcon sx={{ fontSize: 20, color: "#4D6BF6" }} />}
          title="BOQs"
          subtitle="Relevant BOQ items linked to this milestone's activities"
        >
          {materials.length === 0 ? (
            <Typography sx={{ fontSize: 13, color: "#949DAA" }}>
              No BOQ items linked to these activities.
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {materials.map((m) => (
                <BoqItemRow key={m.id} boq={m} />
              ))}
            </Box>
          )}
        </SectionCard>

        {/* Activities — pulled from project state (this milestone's activities) */}
        <SectionCard
          icon={<ConstructionOutlinedIcon sx={{ fontSize: 20, color: "#4D6BF6" }} />}
          title="Activities"
          subtitle="Relevant activities for this milestone"
        >
          {activities.length === 0 ? (
            <Typography sx={{ fontSize: 13, color: "#949DAA" }}>
              No activities for this milestone.
            </Typography>
          ) : (
            <ActivitiesTable activities={activities} />
          )}
        </SectionCard>

        {/* Issues — snags logged against this milestone's activities */}
        <SectionCard
          icon={<ReportProblemOutlinedIcon sx={{ fontSize: 20, color: "#4D6BF6" }} />}
          title="Issues"
          subtitle="Snags logged against this milestone's activities"
        >
          {snags.length === 0 ? (
            <Typography sx={{ fontSize: 13, color: "#949DAA" }}>
              No issues logged for this milestone.
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              {snags.map((s) => (
                <SnagRow key={s.id} snag={s} />
              ))}
            </Box>
          )}
        </SectionCard>

        {aiSummary && (
          <SectionCard title="AI Summary">
            <Typography sx={{ fontSize: 13, color: "#374151", whiteSpace: "pre-wrap" }}>
              {aiSummary}
            </Typography>
          </SectionCard>
        )}

        {/* Delivery Proofs — what the contractor adds */}
        <UploadGrid
          title="Delivery Proofs"
          subtitle="Upload images of the completed work"
          icon={<VerifiedOutlinedIcon sx={{ fontSize: 20, color: "#4D6BF6" }} />}
          pdfFiles={[]}
          onPdfFilesChange={() => {}}
          images={proofs}
          onImagesChange={setProofs}
          readOnly={isReadOnly}
        />

        {/* Invoice — single PDF, required */}
        <UploadGrid
          title="Invoice"
          subtitle="Upload a single PDF (required)"
          icon={<RequestQuoteOutlinedIcon sx={{ fontSize: 20, color: "#4D6BF6" }} />}
          pdfFiles={invoice}
          onPdfFilesChange={(arr) => setInvoice((arr || []).slice(0, 1))}
          images={[]}
          onImagesChange={() => {}}
          readOnly={isReadOnly}
          allowImages={false}
          maxPdfs={1}
        />

        {/* Notes — optional */}
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#0D0D0E", mb: 1 }}>
            Notes (optional)
          </Typography>
          <TextField
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={isReadOnly}
            multiline
            minRows={3}
            fullWidth
            placeholder="Anything the admin / homeowner should know about this delivery"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                backgroundColor: "#fff",
              },
            }}
          />
        </Box>

        {/* Footer actions */}
        {!isReadOnly && (
          <Box sx={{ display: "flex", gap: 1.5, justifyContent: "flex-end", pt: 1 }}>
            <SecondaryButton onClick={backToProject} disabled={saving || submitting}>
              Cancel
            </SecondaryButton>
            <SecondaryButton onClick={handleSave} disabled={saving || submitting}>
              {saving ? "Saving..." : "Save Draft"}
            </SecondaryButton>
            <PrimaryButton
              onClick={handleSubmit}
              disabled={saving || submitting || invoice.length === 0}
            >
              {submitting
                ? "Submitting..."
                : isRejected
                ? "Re-submit"
                : "Submit"}
            </PrimaryButton>
          </Box>
        )}
      </Box>
    </Box>
  );
}
